import { v4 as uuidv4 } from 'uuid';

import { IKnmAct248 } from 'APIPackage/KnmModule/interfaces/IKnmAct248';
import { createAcquaintanceTypeSelectOption } from 'CorePackage/CatalogsModule/common/createAcquaintanceTypeSelectOption';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { createAcquaintanceLackTypeSelectOption } from 'CorePackage/CatalogsModule/common/createAcquaintanceLackTypeSelectOption';
import { IKnmActSingerSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActSingerSelectOption';
import { createKnmExpertTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmExpertTypeSelectOption';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';

import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { createOrganizationTemplateSelectOption } from './createOrganizationTemplateSelectOption';
import { getSelectOption } from './getSelectOption';
import { createKnmActTypeSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmActTypeSelectOption';
import { createKnmCanceledReasonSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmCanceledReasonSelectOption';
import { convertInitialKnmDataEventsToFormData } from './convertInitialKnmDataEventsToFormData';
import { convertKnmDataViolatedRequirementsToFormValues } from './convertKnmDataViolatedRequirementsToFormValues';
import { IKnmActFamiliarizationNotificationsForm } from '../interfaces/IKnmActFamiliarizationNotificationsForm';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { convertActRemarksToFormValues } from './convertActRemarksToFormValues';
import { convertSignersToFormValues } from './convertSignersToFormValues';

interface IConvertKnmDataActToFormValuesParamsGenerator {
  state: IRootState;
  act: IKnmAct248;
  actOrganization: IKnmErknmFormSubject;
  created: string | null | undefined;
  passportEvents?: IKnmErknmFormEvent[];
}

export function convertKnmDataActToFormValues({
  state,
  act,
  actOrganization,
  created,
  passportEvents,
}: IConvertKnmDataActToFormValuesParamsGenerator): IKnmErknmFormAct {
  const titleSignerSelectOption = act.titleSigner?.dictId
    ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
        state,
        act.titleSigner?.dictId,
        act.titleSigner?.dictVersionId,
      )
    : undefined;
  const resultContentPositionCancelOption = act.resultContentPositionCancel?.dictId
    ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
        state,
        act.resultContentPositionCancel?.dictId,
        act.resultContentPositionCancel?.dictVersionId,
      )
    : undefined;

  const knoInspectors = act.knoInspectors?.map(knoInspector => {
    const positionSelectOption = knoInspector.position?.dictId
      ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          knoInspector.position?.dictId,
          knoInspector.position?.dictVersionId,
        )
      : undefined;
    const formKnoInspector: IKnmErknmFormInspector = {
      ...knoInspector,
      position: positionSelectOption,
      listId: knoInspector.guid || uuidv4(),
      label: `${knoInspector.fullName} - ${positionSelectOption?.label}`,
    };

    return formKnoInspector;
  });

  const knoInspectorsOptions: IKnmErknmFormInspector[] | undefined = knoInspectors?.map(i => ({
    listId: i.guid || uuidv4(),
    guid: i.guid || undefined,
    knoInspector: {
      id: i?.guid || '',
      label: `${i.fullName} - ${i.position?.label}`,
      value: i,
    },
    fullName: i.fullName,
    position: i.position,
    dateCreate: i.dateCreate || undefined,
  }));

  const settings = getAdminFetchSettings(state);

  const isShow4122 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM,
  );

  let fioAndPositionSinger: IKnmActSingerSelectOption | undefined;

  if (act.fioSigner && titleSignerSelectOption) {
    fioAndPositionSinger = {
      id: '',
      label: `${act.fioSigner} - ${titleSignerSelectOption?.label}`,
      value: {
        dateCreate: null,
        fullName: act.fioSigner,
        guid: undefined,
        listId: '',
        position: titleSignerSelectOption,
        label: '',
      },
    };

    if (knoInspectors && isShow4122) {
      const singer = knoInspectors?.find(
        i => i.fullName === act.fioSigner && i.position?.value.dictId === act.titleSigner?.dictId,
      );

      if (singer) {
        fioAndPositionSinger = {
          id: singer?.guid || '',
          label: `${singer.fullName} - ${singer.position?.label}`,
          value: singer,
        };
      }
    }
  }

  return {
    acquaintanceLackNoInfoComment: act.acquaintanceLackNoInfoComment || undefined,
    acquaintanceLackType: act.acquaintanceLackType
      ? createAcquaintanceLackTypeSelectOption(act.acquaintanceLackType)
      : undefined,
    acquaintanceType: act.acquaintanceType ? createAcquaintanceTypeSelectOption(act.acquaintanceType) : undefined,
    canceledReason: act.canceledReason ? createKnmCanceledReasonSelectOption(act.canceledReason) : undefined,
    canceledReasonDescription: act.canceledReasonDescription || undefined,
    canceledReasonDescriptionReactQuillEditor: handleReactQuillDeltaValue(
      act.canceledReasonDescriptionReactQuillEditor || act.canceledReasonDescription || undefined,
    ),
    created: act.created || undefined,
    createdBy: act.createdBy || undefined,
    creationActDateTime: convertInitialKnmDateToFormDate(
      act.creationActDateTime,
      DATE_FORMAT_LONG_MINUTE_FNS,
      DATE_FORMAT_ISO_FNS,
    ),
    signActDateTime: act.signActDateTime || undefined,
    document: act.document
      ? {
          active: act.document.active || undefined,
          attachments: act.document.attachments || undefined,
          correlationToken: act.document.correlationToken || undefined,
          description: act.document.description || undefined,
          guid: act.document.guid || undefined,
          type: act.document.type || undefined,
        }
      : undefined,
    durationDays: typeof act.durationDays === 'number' ? act.durationDays.toString() : undefined,
    durationHours: typeof act.durationHours === 'number' ? act.durationHours.toString() : undefined,
    events: act.events ? convertInitialKnmDataEventsToFormData(act.events, state) : undefined,
    fioAndPositionSinger,
    fioReader: act.fioReader || undefined,
    fioSigner: act.fioSigner || undefined,
    guid: act.guid,
    holdingDateTime: convertInitialKnmDateToFormDate(
      act.holdingDateTime,
      DATE_FORMAT_LONG_MINUTE_FNS,
      DATE_FORMAT_ISO_FNS,
    ),
    isNew: !!act.isNew,
    isViolatedMandatoryRequirements: booleanSelectOptions.find(item => item.value === act.isViolations),
    isViolationResolved: booleanSelectOptions.find(item => item.value === act.isViolationResolved) || undefined,
    isViolationResolvedNote: act.isViolationResolvedNote || undefined,
    knoInspectors: isShow4122 || act.paperless ? knoInspectorsOptions : knoInspectors,
    involvedExperts: act.involvedExperts?.map(item => {
      const value = {
        ...item,
        title: item.title || undefined,
        type: item.type ? createKnmExpertTypeSelectOption(item.type) : undefined,
        listId: item.guid || uuidv4(),
      };

      return {
        ...value,
        expert: {
          id: item.guid || '',
          label: `${item.title} - ${item.type?.name}`,
          value,
        },
      };
    }),
    involvedWitnesses: !act.involvedWitnesses
      ? undefined
      : act.involvedWitnesses.map(i => ({
          fullName: i.fullName || undefined,
          listId: i.guid || '',
          isNew: false,
        })),
    listId: act.guid || uuidv4(),
    nextWordDayActDateTime: act.nextWordDayActDateTime || undefined,
    noteInvalidate: act.noteInvalidate || undefined,
    fileInvalidate: act.fileInvalidate || undefined,
    numberAct: act.numberAct || undefined,
    organization: actOrganization,
    organizationTemplate: createOrganizationTemplateSelectOption(actOrganization),
    refuserFio: act.refuserFio || undefined,
    refuserTitle: act.refuserTitle || undefined,
    canceled: act.canceled || undefined,
    titleReader: act.titleReader || undefined,
    titleSigner: titleSignerSelectOption,
    typeAct: act.typeAct ? createKnmActTypeSelectOption(act.typeAct) : undefined,
    updated: act.updated || undefined,
    updatedBy: act.updatedBy || undefined,
    violatedMandatoryRequirements: act.violations
      ? convertKnmDataViolatedRequirementsToFormValues(act.violations, passportEvents)
      : undefined,
    familiarizationNotifications:
      act.familiarizationNotifications?.map(x => {
        const notification: IKnmActFamiliarizationNotificationsForm = {
          noticeDate: convertInitialKnmDateToFormDate(x.noticeDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          knmNoticeType: x.knmNoticeType ? createKnmNotificationMethodSelectOption(x.knmNoticeType) : undefined,
          note: x.note,
          listId: x.guid || uuidv4(),
          isNew: !!x.isNew,
          existedAtSigning: x.existedAtSigning || undefined,
        };

        return notification;
      }) || undefined,
    remarks: convertActRemarksToFormValues(act.remarks),
    signed: act.signed ?? undefined,
    paperless: act.paperless || undefined,
    extractVersion: act.extractVersion || undefined,
    officialFullNameCancel: act.officialFullNameCancel || undefined,
    resultContentPositionCancel: resultContentPositionCancelOption,
    firstExtractDateTime:
      convertInitialKnmDateToFormDate(act.firstExtractDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) ||
      null,
    systemSignatureUsed: act.systemSignatureUsed || false,
    systemSignatureTitle: act.systemSignatureTitle || null,
    systemSignatureMnemonic: act.systemSignatureMnemonic || null,
    systemSignatureDateTime: act.systemSignatureDateTime || null,
    signers: convertSignersToFormValues(act.signers, state, act.knoInspectors || []),
  };
}
